import React, { useRef } from 'react';
import { FileText, ListChecks, CheckCircle2, Circle, X, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatMoney } from '../lib/money';

// Detects two taps/clicks within 350ms — works for both mouse and touch.
export const useDoubleTap = (handler) => {
  const last = useRef(0);
  return (e) => {
    const now = Date.now();
    if (now - last.current < 350) { last.current = 0; handler(e); }
    else { last.current = now; }
  };
};

const Overlay = ({ children, onDoubleTap, testId }) => {
  const tap = useDoubleTap(onDoubleTap);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-slate-900/60" onClick={tap} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={tap}>
        {children}
      </div>
    </div>
  );
};

// ---------- Customer invoice (COGS only) ----------
export const CustomerInvoiceTile = ({ order, cogs, currency, expanded, onToggle }) => {
  const tap = useDoubleTap(onToggle);
  const items = order?.items || [];
  const subtotal = Number(order?.subtotal) || 0;
  const tax = Number(order?.tax_amount) || 0;

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={tap}
        className="w-full text-left rounded-xl border-2 border-blue-200 bg-blue-50/40 p-3 hover:border-blue-300 transition-colors cursor-pointer select-none"
        data-testid="qr-invoice-tile"
        title="Double-tap to open the full invoice"
      >
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-blue-700 font-bold">
          <FileText className="h-3.5 w-3.5" /> Customer invoice · COGS
        </div>
        <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="vendor-qr-cogs-amount">
          {formatMoney(cogs, currency)}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">{items.length} item{items.length === 1 ? '' : 's'} · goods + tax only</div>
        <div className="text-[10px] text-blue-600 font-medium mt-2">Double-tap to open</div>
      </button>
    );
  }

  return (
    <Overlay onDoubleTap={onToggle} testId="qr-invoice-expanded">
      <div className="p-5" data-testid="qr-invoice-expanded-body">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-blue-700 font-bold">
              <FileText className="h-3.5 w-3.5" /> Customer invoice · COGS only
            </div>
            <div className="font-mono text-xs text-slate-500 mt-1">{order?.id}</div>
            {order?.customer_name && <div className="text-sm text-slate-700 mt-0.5">{order.customer_name}</div>}
          </div>
          <button onClick={onToggle} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="qr-invoice-close" aria-label="Close invoice">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
          {items.map((it, idx) => (
            <div key={it.id || idx} className="flex items-center justify-between gap-3 py-2 text-sm">
              <div className="min-w-0">
                <span className="font-medium text-slate-900">{it.qty}×</span> <span className="text-slate-700">{it.name}</span>
              </div>
              <div className="text-slate-900 font-medium whitespace-nowrap">
                {formatMoney((Number(it.price) || 0) * (Number(it.qty) || 1), currency)}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600"><span>Subtotal (goods)</span><span>{formatMoney(subtotal, currency)}</span></div>
          <div className="flex justify-between text-slate-600"><span>Tax</span><span>{formatMoney(tax, currency)}</span></div>
          <div className="flex justify-between font-bold text-slate-900 text-base pt-1 border-t border-slate-100">
            <span>Total payable to you</span>
            <span data-testid="qr-invoice-total">{formatMoney(cogs, currency)}</span>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-500 flex items-start gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
          Delivery &amp; service fees are on a separate ShipitNow invoice, reviewed and approved by ShipitNow admin — they are not part of this invoice.
        </div>
        <div className="mt-3 text-center text-[10px] text-slate-400">Double-tap anywhere to close</div>
      </div>
    </Overlay>
  );
};

// ---------- Picking list ----------
export const PickingListTile = ({ order, onPick, onReady, expanded, onToggle }) => {
  const tap = useDoubleTap(onToggle);
  const pl = order?.picking_list || [];
  const items = pl.length ? pl : (order?.items || []).map((i) => ({ name: i.name, qty: i.qty, picked: false }));
  const done = pl.filter((x) => x.picked).length;
  const allPicked = pl.length > 0 && done === pl.length;
  const isPreparing = order?.status === 'preparing';
  const isReady = ['ready', 'ready_for_pickup'].includes(order?.status);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={tap}
        className="w-full text-left rounded-xl border-2 border-amber-200 bg-amber-50/40 p-3 hover:border-amber-300 transition-colors cursor-pointer select-none"
        data-testid="qr-picking-tile"
        title="Double-tap to open the picking list"
      >
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-800 font-bold">
          <ListChecks className="h-3.5 w-3.5" /> Picking list
        </div>
        <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="qr-picking-progress">
          {pl.length ? `${done}/${pl.length}` : items.length} <span className="text-sm font-medium text-slate-500">{pl.length ? 'picked' : 'items'}</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
          {isReady ? 'Order is READY' : isPreparing ? (allPicked ? 'All picked — mark READY' : 'Picking in progress') : 'Start preparing to pick items'}
        </div>
        <div className="text-[10px] text-amber-700 font-medium mt-2">Double-tap to open</div>
      </button>
    );
  }

  return (
    <Overlay onDoubleTap={onToggle} testId="qr-picking-expanded">
      <div className="p-5" data-testid="qr-picking-expanded-body">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-800 font-bold">
              <ListChecks className="h-3.5 w-3.5" /> Picking list {pl.length ? `· ${done}/${pl.length} picked` : ''}
            </div>
            <div className="font-mono text-xs text-slate-500 mt-1">{order?.id}</div>
          </div>
          <button onClick={onToggle} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="qr-picking-close" aria-label="Close picking list">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {!isPreparing && !isReady && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            Move the order to <b>Start preparing</b> to unlock item picking.
          </div>
        )}

        <div className="mt-4 space-y-2">
          {items.map((it, idx) => (
            <div key={it.item_id || idx} className="flex items-center gap-2.5 text-sm rounded-lg border border-slate-100 px-3 py-2">
              {it.picked
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
              <span className={`flex-1 min-w-0 truncate ${it.picked ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                {it.qty}× {it.name}
              </span>
              {pl.length > 0 && isPreparing && (
                <button
                  onClick={(e) => { e.stopPropagation(); onPick(it, idx); }}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${it.picked ? 'bg-white text-slate-500 border-slate-200 hover:border-slate-300' : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'}`}
                  data-testid={`qr-pick-item-${idx}`}
                >
                  {it.picked ? 'UNDO' : 'PICKED'}
                </button>
              )}
            </div>
          ))}
        </div>

        {isReady ? (
          <div className="mt-4 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-sm text-violet-700 font-medium text-center" data-testid="qr-picking-ready-badge">
            Order is READY
          </div>
        ) : isPreparing && (
          <Button
            onClick={(e) => { e.stopPropagation(); onReady(); }}
            className={`mt-4 w-full text-white ${allPicked || !pl.length ? 'bg-violet-600 hover:bg-violet-700' : 'bg-slate-400 hover:bg-slate-500'}`}
            data-testid="qr-picking-ready-btn"
          >
            {allPicked || !pl.length ? 'Next step — order is READY' : `READY anyway (${pl.length - done} unpicked)`}
          </Button>
        )}
        <div className="mt-3 text-center text-[10px] text-slate-400">Double-tap anywhere to close</div>
      </div>
    </Overlay>
  );
};
